import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Search, Edit3, Trash2, Copy, Eye, Save, Rocket, LogOut, X, ArrowLeft, Upload, Loader2, Lock, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import RichEditor from "../components/RichEditor";
import { api, getAdminToken, setAdminToken, clearAdminToken } from "../lib/api";

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "Numerology",
  tags: [], status: "draft", meta_title: "", meta_description: "", faqs: [], scheduled_at: "",
};

function authHeaders() { return { Authorization: `Bearer ${getAdminToken()}` }; }

export default function BlogAdmin() {
  const navigate = useNavigate();
  const coverInputRef = useRef(null);
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [logging, setLogging] = useState(false);
  const [posts, setPosts] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null = list view
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const loadPosts = useCallback(async () => {
    const { data } = await api.get("/admin/blog", { headers: authHeaders() });
    setPosts(data);
  }, []);

  useEffect(() => {
    const t = getAdminToken();
    if (!t) { setChecking(false); return; }
    api.get("/admin/verify", { headers: authHeaders() })
      .then(() => { setAuthed(true); setChecking(false); loadPosts(); })
      .catch(() => { clearAdminToken(); setChecking(false); });
  }, [loadPosts]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const { data } = await api.post("/admin/login", { password: pw });
      setAdminToken(data.token);
      setAuthed(true);
      setPw("");
      toast.success("Welcome back, editor.");
      loadPosts();
    } catch {
      toast.error("Incorrect password.");
      setPw("");
    } finally {
      setLogging(false);
    }
  };

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p, scheduled_at: p.scheduled_at || "" }); setEditing(p.id); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (status) => {
    if (!form.title.trim()) return toast.error("Please add a title.");
    setSaving(true);
    const payload = { ...form, status };
    try {
      let res;
      if (editing === "new") res = await api.post("/admin/blog", payload, { headers: authHeaders() });
      else res = await api.put(`/admin/blog/${editing}`, payload, { headers: authHeaders() });
      toast.success(status === "published" ? "Published! Live on the blog now." : "Draft saved.");
      setForm({ ...EMPTY, ...res.data });
      setEditing(res.data.id);
      loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await api.delete(`/admin/blog/${id}`, { headers: authHeaders() });
    toast.success("Deleted.");
    loadPosts();
  };

  const duplicate = async (id) => {
    await api.post(`/admin/blog/${id}/duplicate`, {}, { headers: authHeaders() });
    toast.success("Duplicated as draft.");
    loadPosts();
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      set("cover_image", process.env.REACT_APP_BACKEND_URL + data.url);
      toast.success("Cover uploaded.");
    } catch { toast.error("Upload failed."); }
  };

  const addTag = (e) => {
    e.preventDefault();
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      set("tags", [...form.tags, tagInput.trim()]);
    }
    setTagInput("");
  };

  const logout = () => { clearAdminToken(); setAuthed(false); navigate("/blog"); };

  if (checking) return <div className="pt-40 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>;

  // ---------- LOGIN SCREEN ----------
  if (!authed) {
    return (
      <div className="flex min-h-[85vh] items-center justify-center px-5 pt-24">
        <SEO title="Editor Access" description="Numoraa editor" path="/blog-admin" />
        <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-8 shadow-lift text-center" data-testid="admin-login-card">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <Lock className="h-6 w-6 text-gold" strokeWidth={1.6} />
          </span>
          <h1 className="mt-6 font-serif text-3xl">Editor Access</h1>
          <p className="mt-2 text-sm text-muted-foreground">Enter your password to open the Blog Admin Dashboard.</p>
          <form onSubmit={doLogin} className="mt-6 space-y-4">
            <input
              type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Password"
              className="w-full rounded-xl border border-border bg-background px-4 py-3 text-center outline-none focus:border-primary"
              data-testid="admin-login-password"
            />
            <button disabled={logging} data-testid="admin-login-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground transition-all hover:shadow-gold disabled:opacity-60">
              {logging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-gold" />} Unlock Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  const filtered = posts.filter((p) => p.title.toLowerCase().includes(query.toLowerCase()));

  // ---------- LIST VIEW ----------
  if (editing === null) {
    return (
      <div className="pt-28 md:pt-32 pb-24 min-h-screen">
        <SEO title="Blog Admin" description="Numoraa editor" path="/blog-admin" />
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Editor Dashboard</p>
              <h1 className="mt-2 font-serif text-4xl md:text-5xl font-light tracking-tighter">Manage the Journal</h1>
            </div>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:border-primary"><LogOut className="h-4 w-4" /> Logout</button>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 sm:w-72">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search posts…" className="flex-1 bg-transparent text-sm outline-none" data-testid="admin-search" />
            </div>
            <button onClick={openNew} data-testid="admin-new-post" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground transition-all hover:-translate-y-0.5 hover:shadow-gold"><Plus className="h-4 w-4" /> New Post</button>
          </div>

          <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-card shadow-soft" data-testid="admin-post-list">
            {filtered.length === 0 ? (
              <p className="p-8 text-center text-muted-foreground">No posts yet. Create your first one!</p>
            ) : filtered.map((p) => (
              <div key={p.id} className="flex items-center gap-4 border-b border-border p-4 last:border-0 hover:bg-secondary/40">
                <div className="h-12 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary">
                  {p.cover_image && <img src={p.cover_image} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{p.title || "Untitled"}</p>
                  <p className="text-xs text-muted-foreground">{p.category} · {p.reading_time} min · <span className={p.status === "published" ? "text-gold" : "text-muted-foreground"}>{p.status}</span></p>
                </div>
                <div className="flex items-center gap-1">
                  {p.status === "published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-secondary" title="View"><Eye className="h-4 w-4" /></a>}
                  <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-secondary" title="Edit"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => duplicate(p.id)} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-secondary" title="Duplicate"><Copy className="h-4 w-4" /></button>
                  <button onClick={() => remove(p.id)} className="flex h-9 w-9 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- EDITOR VIEW ----------
  return (
    <div className="pt-24 md:pt-28 pb-24 min-h-screen">
      <SEO title="Edit Post" description="Numoraa editor" path="/blog-admin" />
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button onClick={() => { setEditing(null); loadPosts(); }} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to posts</button>
          <div className="flex items-center gap-2">
            {form.slug && form.status === "published" && (
              <a href={`/blog/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:border-primary"><Eye className="h-4 w-4" /> Preview</a>
            )}
            <button onClick={() => save("draft")} disabled={saving} data-testid="admin-save-draft" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm hover:border-primary disabled:opacity-60"><Save className="h-4 w-4" /> Save Draft</button>
            <button onClick={() => save("published")} disabled={saving} data-testid="admin-publish" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground transition-all hover:shadow-gold disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4 text-gold" />} Publish</button>
          </div>
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8 space-y-6">
            <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Post title…" data-testid="admin-title"
              className="w-full bg-transparent font-serif text-4xl font-light tracking-tight outline-none placeholder:text-muted-foreground/40" />
            <textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="Short excerpt / summary…" rows={2} data-testid="admin-excerpt"
              className="w-full resize-none rounded-xl border border-border bg-card p-4 text-sm outline-none focus:border-primary" />
            <RichEditor value={form.content} onChange={(v) => set("content", v)} />

            {/* FAQs */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-xl">FAQs</h3>
                <button onClick={() => set("faqs", [...form.faqs, { question: "", answer: "" }])} className="inline-flex items-center gap-1.5 text-sm text-primary"><Plus className="h-4 w-4" /> Add FAQ</button>
              </div>
              <div className="mt-4 space-y-4">
                {form.faqs.map((f, i) => (
                  <div key={i} className="rounded-xl border border-border p-4">
                    <div className="flex items-center gap-2">
                      <input value={f.question} onChange={(e) => { const n = [...form.faqs]; n[i].question = e.target.value; set("faqs", n); }} placeholder="Question" className="flex-1 bg-transparent text-sm font-medium outline-none" />
                      <button onClick={() => set("faqs", form.faqs.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                    </div>
                    <textarea value={f.answer} onChange={(e) => { const n = [...form.faqs]; n[i].answer = e.target.value; set("faqs", n); }} placeholder="Answer" rows={2} className="mt-2 w-full resize-none bg-transparent text-sm text-muted-foreground outline-none" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar settings */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Cover Image</p>
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadCover(e.target.files[0]); e.target.value = ""; }} data-testid="admin-cover-upload" />
              {form.cover_image ? (
                <div className="relative mt-3">
                  <img src={form.cover_image} alt="" className="w-full rounded-xl object-cover" />
                  <button type="button" onClick={() => set("cover_image", "")} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/90"><X className="h-4 w-4" /></button>
                  <button type="button" onClick={() => coverInputRef.current?.click()} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs hover:border-primary" data-testid="admin-cover-replace-btn">
                    <Upload className="h-4 w-4" /> Replace with image from device
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => coverInputRef.current?.click()} className="mt-3 flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border py-8 hover:border-primary" data-testid="admin-cover-upload-btn">
                  <Upload className="h-5 w-5 text-muted-foreground" /><span className="mt-2 text-xs font-medium text-foreground">Upload image from your device</span>
                  <span className="mt-0.5 text-[11px] text-muted-foreground">JPG, PNG, WebP — click to choose a file</span>
                </button>
              )}
              <input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="or paste an image URL" className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-xs outline-none focus:border-primary" />
            </div>

            <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</label>
                <input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" data-testid="admin-category" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tags</label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {form.tags.map((t) => (
                    <span key={t} className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs">{t}<button onClick={() => set("tags", form.tags.filter((x) => x !== t))}><X className="h-3 w-3" /></button></span>
                  ))}
                </div>
                <form onSubmit={addTag}><input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag + Enter" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" data-testid="admin-tag-input" /></form>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">URL Slug</label>
                <input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" data-testid="admin-slug" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Schedule (optional)</label>
                <input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
              <p className="text-xs uppercase tracking-[0.15em] text-gold">SEO</p>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Meta Title</label>
                <input value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} placeholder="Defaults to title" className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" data-testid="admin-meta-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Meta Description</label>
                <textarea value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} rows={3} placeholder="Defaults to excerpt" className="mt-2 w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" data-testid="admin-meta-desc" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
